<template>
    <v-container>
        <v-row no-gutters class="mb-5">
            <v-col cols="12">
                <v-card>
                    <v-container class="px-3 py-0" v-if="searchParams">
                        <v-row align="center">
                            <v-col>
                                <v-autocomplete
                                    label="Unit"
                                    item-value="id"
                                    item-text="number"
                                    :items="units"
                                    no-filter
                                    :loading="isSearchingUnit"
                                    :search-input.sync="unitSearch"
                                    v-model="searchParams.unitId"
                                    no-data-text="Start typing a Unit Name or Number"
                                    name="unit"
                                    autocomplete="off"
                                    hide-details
                                    :clearable="true"
                                >
                                    <template v-slot:item="{ item }">
                                        <v-list-item-content>
                                            <v-list-item-title>{{ item.number }} ({{ item.garage }})</v-list-item-title>
                                            <v-list-item-subtitle v-text="item.name"></v-list-item-subtitle>
                                        </v-list-item-content>
                                    </template>
                                </v-autocomplete>
                            </v-col>
                            <v-col>
                                <v-select v-model="searchParams.types" :items="types" label="Types" multiple chips deletable-chips hide-details :clearable="true"></v-select>
                            </v-col>
                        </v-row>
                        <v-row align="center">
                            <v-col>
                                <v-autocomplete
                                    label="Garage"
                                    item-value="id"
                                    item-text="location"
                                    :items="userGarageLocations"
                                    v-model="searchParams.garageLocationIds"
                                    v-validate="'required'"
                                    name="garage"
                                    required
                                    multiple
                                    chips
                                    deletable-chips
                                    clearable
                                >
                                    <template v-slot:selection="{ attrs, item, index }">
                                        <v-chip v-bind="attrs" v-if="index < 6" close @click:close="() => searchParams.garageLocationIds.splice(index, 1)">
                                            <span>{{ item.location }}</span>
                                        </v-chip>
                                        <span v-if="index === 6" class="grey--text caption">(+{{ searchParams.garageLocationIds.length - 6 }} others)</span>
                                    </template>
                                </v-autocomplete>
                            </v-col>
                        </v-row>
                        <v-row align="center">
                            <v-col>
                                <v-menu v-model="startDateDialog" :close-on-content-click="false" max-width="290">
                                    <template v-slot:activator="{ on }">
                                        <v-text-field
                                            label="Start Date"
                                            readonly
                                            v-model="formattedStartDate"
                                            name="received date"
                                            v-on="on"
                                            append-icon="event"
                                            hide-details
                                            @click:append="startDateDialog = true"
                                            :clearable="true"
                                        ></v-text-field>
                                    </template>
                                    <v-date-picker no-title v-model="searchParams.from" @change="startDateDialog = false"></v-date-picker>
                                </v-menu>
                            </v-col>
                            <v-col>
                                <v-menu v-model="endDateDialog" :close-on-content-click="false" max-width="290">
                                    <template v-slot:activator="{ on }">
                                        <v-text-field v-model="formattedEndDate" label="End Date" readonly name="end date" v-on="on" append-icon="event" hide-details @click:append="endDateDialog = true" :clearable="true"></v-text-field>
                                    </template>
                                    <v-date-picker no-title v-model="searchParams.to" @change="endDateDialog = false"></v-date-picker>
                                </v-menu>
                            </v-col>
                            <v-col cols="auto">
                                <v-checkbox v-model="searchParams.showNhvasUnitsOnly">
                                    <template v-slot:label
                                        >Show NHVAS units only</template
                                    >
                                </v-checkbox>
                                <v-checkbox v-if="hasDefectTypeSelected" v-model="searchParams.filterDefectsByScheduledDate" class="mt-0">
                                    <template v-slot:label
                                        >Show Scheduled Defects</template
                                    >
                                </v-checkbox>
                            </v-col>
                            <v-col class="shrink">
                                <v-btn text icon @click.native="performSearch">
                                    <v-icon>mdi-magnify</v-icon>
                                </v-btn>
                            </v-col>
                            <v-col class="shrink pr-0 pl-1">
                                <v-btn text icon @click.native="clearSearch">
                                    <v-icon>clear</v-icon>
                                </v-btn>
                            </v-col>
                        </v-row>
                    </v-container>
                </v-card>
            </v-col>
        </v-row>
        <v-row class="mx-0 pt-3 pb-3">
            <v-spacer></v-spacer>
            <v-menu offset-y>
                <template v-slot:activator="{ on }">
                    <v-btn small tile v-on="on"> <v-icon>save_alt</v-icon>&nbsp;Export </v-btn>
                </template>

                <v-list>
                    <v-list-item @click="exportCsv()">
                        <v-list-item-title>
                            <v-icon>grid_on</v-icon>&nbsp;Excel (CSV)
                            <v-chip x-small>limit {{ maxRowsPerCsv }} rows</v-chip>
                        </v-list-item-title>
                    </v-list-item>
                </v-list>
            </v-menu>
        </v-row>
        <v-card>
            <v-data-table
                :items="reportTasks"
                :items-per-page="options.itemsPerPage"
                :headers="headers"
                :loading="$wait.is(['loading reports'])"
                :footer-props.sync="footerProps"
                :options.sync="options"
                :server-items-length="serverCount"
                :multi-sort="false"
            >
                <v-progress-linear v-slot:progress color="blue" indeterminate></v-progress-linear>
                <template v-slot:item="{ item, isExpanded, expand }">
                    <tr>
                        <td>
                            <router-link class="accent--text" :to="{ name: 'unit', params: { unitId: item.unitId } }">{{ item.unitNumber }}</router-link>
                        </td>
                        <td>{{ item.garage }}</td>
                        <td>{{ item.type }}</td>
                        <td v-if="item.notes">{{ item.description + ' // ' + item.notes }} <v-icon>pending_actions</v-icon></td>
                        <td v-else>{{ item.description }}</td>
                        <td>{{ item.createdDate | formatDate('DD MMM YYYY') }}</td>
                        <td>{{ item.dueDate | formatDate('DD MMM YYYY') }}</td>
                        <td>{{ item.completedDate | formatDate('DD MMM YYYY') }}</td>
                    </tr>
                </template>
                <template v-slot:no-results>
                    <v-layout row align-center>
                        <v-flex>No matching records found</v-flex>
                    </v-layout>
                </template>
                <template v-slot:expanded-item="{ item, headers }">
                    <td :colspan="headers.length" class="pa-3">
                        <v-simple-table dark>
                            <template v-slot:default>
                                <thead>
                                    <tr>
                                        <th class="text-left">Field</th>
                                        <th class="text-left">Old Value</th>
                                        <th class="text-left">New Value</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="x in item.propertyChanges" :key="x.propertyName">
                                        <td>{{ x.propertyName }}</td>
                                        <td>{{ x.oldValue }}</td>
                                        <td>{{ x.newValue }}</td>
                                    </tr>
                                </tbody>
                            </template>
                        </v-simple-table>
                    </td>
                </template>
            </v-data-table>
        </v-card>
        <v-overlay :value="!searchParams" absolute>
            <div class="text-center">
                <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
                <div class="mt-2">Loading reports...</div>
            </div>
        </v-overlay>
    </v-container>
</template>

<script>
import moment from 'moment';
import { DateFormat } from '@/plugins/date-filter';
import DownloadHelper from '@/lib/DownloadHelper';
import ReportClient from '@/components/Reports/ReportsClient';
import UsersClient from '@/components/Users/UsersClient.vue';
import Garages from '@/components/GarageLocations/GarageLocationsClient';

export default {
    mixins: [ReportClient, UsersClient, Garages],
    data() {
        return {
            user: null,
            maxRowsPerCsv: 1000,
            reportTasks: [],
            serverCount: 0,
            units: [],
            isSearchingUnit: false,
            unitSearch: '',
            selectedUnit: null,
            search: '',
            startDateDialog: false,
            endDateDialog: false,
            expanded: [],
            options: {
                itemsPerPage: 20,
                sortBy: ['completedDate'],
                sortDesc: [true]
            },
            footerProps: {
                itemsPerPageOptions: [20, 100, 200]
            },
            headers: [
                { text: 'Unit', value: 'unitNumber' },
                { text: 'Garage', value: 'garage' },
                { text: 'Type', value: 'type' },
                { text: 'Description', value: 'description', sortable: false },
                { text: 'Created', value: 'createdDate' },
                { text: 'Scheduled', value: 'dueDate' },
                { text: 'Completed', value: 'completedDate', sortable: true }
            ],
            searchParams: null,
            types: ['Services', 'Date-Based Tasks', 'Defects', 'Campaigns']
        };
    },
    computed: {
        hasDefectTypeSelected() {
            return Array.isArray(this.searchParams && this.searchParams.types) && this.searchParams.types.includes('Defects');
        },
        formattedStartDate: {
            get() {
                return this.searchParams.from ? moment(this.searchParams.from).format(DateFormat.DefaultDisplay) : '';
            },
            set() {
                //set when clearing
                this.searchParams.from = null;
            }
        },
        formattedEndDate: {
            get() {
                return this.searchParams.to ? moment(this.searchParams.to).format(DateFormat.DefaultDisplay) : '';
            },
            set() {
                //set when clearing
                this.searchParams.to = null;
            }
        }
    },
    methods: {
        async performSearch() {
            this.$wait.start('loading reports');

            let results = await this.searchReports(this.getSearchParams());
            this.reportTasks = results ? results.data : [];
            this.serverCount = results ? results.total : 0;

            this.$wait.end('loading reports');
        },
        getSearchParams() {
            const { sortBy, sortDesc, page, itemsPerPage } = this.options;

            const searchParams = {
                ...this.searchParams,
                page,
                pageSize: itemsPerPage,
                orderBy: sortBy.length ? sortBy[0] : null,
                orderByDescending: sortDesc.length ? sortDesc[0] : false
            };

            if (!this.hasDefectTypeSelected) {
                delete searchParams.filterDefectsByScheduledDate;
            }

            return searchParams;
        },
        async clearSearch() {
            this.resetSearch();
            await this.performSearch();
        },
        resetSearch() {
            this.searchParams = {
                unitId: '',
                garageLocationIds: this.userGarages,
                types: ['Services', 'Date-Based Tasks', 'Defects', 'Campaigns'],
                from: null,
                to: null,
                showNhvasUnitsOnly: false,
                filterDefectsByScheduledDate: false
            };
        },
        performUnitSearch: _.debounce(async (val, me) => {
            if (!me.unitSearch) {
                me.unitSearch = me.selectedUnit ? me.selectedUnit.name : '';
            }
            if (!me.unitSearch) {
                return;
            }

            try {
                me.isSearchingUnit = true;
                const response = await me.$api.get(`units?searchTerm=${me.unitSearch}&unitType='both'&orderByUnitMatch=true`);
                me.units = response.data.data;
            } catch {
                me.$toast.error('There was an error getting units');
            }
            me.isSearchingUnit = false;
        }, 200),
        entityName(name) {
            const parts = name.split('.');
            return parts[parts.length - 1];
        },
        async exportCsv() {
            let searchParams = this.getSearchParams();
            searchParams.page = 1;
            searchParams.pageSize = this.maxRowsPerCsv;

            const results = await this.searchReports(searchParams);
            const data = results ? results.data : [];

            const rows = data.map(x => {
                return [
                    x.unitMasterKey || x.unitNumber,
                    x.truckName,
                    x.department,
                    x.activityName,
                    x.vin,
                    x.registrationNumber,
                    x.garage,
                    x.installationAddress,
                    x.type,
                    x.incidentNumber,
                    x.notes ? `${x.description} // ${x.notes} (Scheduled)` : x.description,
                    x.hoursWorked,
                    x.hours,
                    x.kilometres,
                    x.completedBy,
                    x.source,
                    x.category,
                    x.createdDate ? moment(x.createdDate).format(DateFormat.DefaultDisplay) : '',
                    x.receivedDate ? moment(x.receivedDate).format(DateFormat.DefaultDisplay) : '',
                    x.dueDate ? moment(x.dueDate).format(DateFormat.DefaultDisplay) : null,
                    x.scheduledReasonCategory,
                    x.scheduledReasonNotes,
                    x.completedDate ? moment(x.completedDate).format(DateFormat.DefaultDisplay) : '',
                    x.isComplete
                ];
            });

            const csv = DownloadHelper.makeCsv(
                [
                    'Unit',
                    'Unit Name',
                    'Department',
                    'Activity Name',
                    'VIN',
                    'Registration',
                    'Garage',
                    'Installation Address',
                    'Task Type',
                    'Incident/Reference #',
                    'Description',
                    'Time Taken',
                    'Hours',
                    'Kilometres',
                    'Completed By',
                    'Source',
                    'Category',
                    'Created',
                    'Received',
                    'Scheduled',
                    'Scheduled Reason Category',
                    'Scheduled Reason Notes',
                    'Completed',
                    'IsComplete'
                ],
                rows
            );
            DownloadHelper.download(`Report_${moment().format(DateFormat.Canonical)}.csv`, new Blob([csv]));
        }
    },
    watch: {
        async unitSearch(val) {
            this.performUnitSearch(val, this);
        },
        options: {
            async handler() {
                await this.performSearch();
            },
            deep: true
        }
    },
    async mounted() {
        await this.loadUserGarages();
        await this.getGarageSelections();
        this.maxRowsPerCsv = await this.loadCsvExportLimit();
        this.resetSearch();
    }
};
</script>

<style lang="scss" scoped>
.v-menu.v-menu--inline {
    display: inline !important;
}
</style>
